
import Logger from "../lib/logger";
import KeenSlider from 'keen-slider';

class ThemeBlogGrid {

	// Logic
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	/**
	 * Class static initialization block
	 */
	constructor(dom, infiniteScrollable, mobileSlidable) {

		this.NAME = 'ThemeBlogGrid';
		Logger.info(`${this.NAME} > init`);

		const self = this;

		// Assign variables
		this.dom = dom;
		this.gridContainer = this.dom.querySelector('[data-theme-blog-grid-grid]');
		this.hash = Math.random().toString(36).substr(2, 9);

		// Mobile slider
		this.mobileSlidableSelector = 'data-theme-blog-grid-mobile-slidable';
		if (this.dom.getAttribute(this.mobileSlidableSelector) !== null && typeof this.dom.getAttribute(this.mobileSlidableSelector) != 'undefined') {
			Logger.info(`${this.NAME} > init > Configuring as mobile slidable`);
			this.mobileSlider = this.dom.querySelector('[data-theme-blog-grid-grid]');
			this.mobileSliderSlideSelector = '.theme-blog-grid__item';
			this.initMobileSlider();
		}

		// Infinite Scrollable only
		this.infiniteScrollable = infiniteScrollable;
		if (this.infiniteScrollable) {
			Logger.info(`${this.NAME} > init > Configuring as infinite scrollable`);

			this.adjustPageParameter = typeof this.infiniteScrollable.adjustPageParameter != 'undefined' ? this.infiniteScrollable.adjustPageParameter : true;
			this.authorId = this.infiniteScrollable.authorId ? this.infiniteScrollable.authorId : null;
			this.bookend = this.dom.querySelector('[data-theme-blog-grid-bookend]');
			this.bookendHiddenClass = 'theme-blog-grid__bookend--hidden';
			this.categoryId = this.infiniteScrollable.categoryId ? this.infiniteScrollable.categoryId : null;
			this.tagId = this.infiniteScrollable.tagId ? this.infiniteScrollable.tagId : null;
			this.page = this.infiniteScrollable.page ? this.infiniteScrollable.page : 1;

			/**
			 * An observer for the bookend
			 **/
			this.bookendObserver = new IntersectionObserver(entries => {
				entries.forEach(entry => {
					const intersecting = entry.isIntersecting;
					if (intersecting) {
						Logger.info(`${this.NAME} > bookendObserver > Is intersecting`);
						self.gatherNextPageOfGridItems();
					}
				})
			});

			/**
			 * An observer for the pagination markers
			 **/
			this.paginationObserver = new IntersectionObserver(entries => {
				entries.forEach(entry => {
					const intersecting = entry.isIntersecting;
					if (intersecting) {
						Logger.info(`${this.NAME} > paginationObserver > Is intersecting`);
						const page = entry.target.getAttribute(`data-theme-grid-pagination-marker`);
						self.setPaginationUrlParameter(page);
					}
				})
			});

			this.markPage(this.page, true);
			this.initBookendObserver();
			this.initPaginationObserver();
		}
	}

	appendGridItems(gridItems) {
		Logger.info(`${this.NAME} > appendGridItems`);

		const self = this;

		if (!Array.isArray(gridItems) || !gridItems.length) {
			return;
		}

		gridItems.forEach((item) => {
			this.gridContainer.innerHTML += `<div class="theme-blog-grid__item">${item}</div>`;
		});
	}

	/**
	 * Gather a batch of slides for the first section
	 **/
	async gatherNextPageOfGridItems() {
		Logger.info(`${this.NAME} > gatherNextPageOfGridItems`);
		this.page++;
		const html = await this.requestGridItems();

		if (Array.isArray(html) && html.length > 0) {
			this.markPage(this.page);
			this.appendGridItems(html);
			this.setPaginationUrlParameter(this.page);
			this.initPaginationObserver();
		} else {
			this.bookend.classList.add(this.bookendHiddenClass);
		}
	}

	getPaginationMarkers() {
		Logger.info(`${this.NAME} > getPaginationMarkers`);
		return this.dom.querySelectorAll('[data-theme-grid-pagination-marker]');
	}

	/**
	 * Init the bookend observer
	 **/
	initBookendObserver() {
		Logger.info(`${this.NAME} > initBookendObserver`);
		this.bookendObserver.observe(this.bookend);
	}

	/**
	 * Init the pagination observer
	 **/
	initPaginationObserver() {
		Logger.info(`${this.NAME} > initPaginationObserver`);
		const self = this;
		const paginationMarkers = this.getPaginationMarkers();
		if (paginationMarkers) {
			paginationMarkers.forEach((marker) => {
				self.paginationObserver.observe(marker);
			});
		}
	}

	/**
	 * Initialize the mobile slider
	 **/
	initMobileSlider() {

		Logger.info(`${this.NAME} > initMobileSlider`);

		var slider = new KeenSlider(this.mobileSlider, {
			selector: this.mobileSliderSlideSelector,
			loop: true,
			breakpoints: {
				'(min-width: 960px)': {
					disabled: true
				},
			},
			slides: {
				perView: 1,
				spacing: 12
			},
			created: () => {
				Logger.info(`${this.NAME} > initMobileSlider > created`);
			},
			slideChanged: (props) => {
				const idx = props.track.details.rel;
				Logger.info(`${this.NAME} > initMobileSlider > slideChanged > ${idx}`);
			}
		});
	}

	markPage(page, prepend) {
		Logger.info(`${this.NAME} > markPage > ${page}`);

		let marker;

		// If even, add an ad
		if (page % 2 == 0) {
			marker = `<div id="${this.hash}-${page}" class="theme-blog-grid__ad" data-theme-grid-pagination-marker="${page}" align="center">
					   <!-- HJ - Theme Blog Grid - Display -->
					   <ins class="adsbygoogle"
							style="display:block"
							data-ad-client="ca-pub-0165093894345076"
							data-ad-slot="4387513826"
							data-ad-format="auto"
							data-full-width-responsive="true"></ins></div>`;
			if (prepend) {
				this.gridContainer.innerHTML = marker + this.gridContainer.innerHTML;
			} else {
				this.gridContainer.innerHTML += marker;
			}
			(adsbygoogle = window.adsbygoogle || []).push({});

			// Otherwise add a marker
		} else {
			marker = `<span id="${this.hash}-${page}" class="theme-blog-grid__pagination-marker" data-theme-grid-pagination-marker="${page}"></span>`;
			if (prepend) {
				this.gridContainer.innerHTML = marker + this.gridContainer.innerHTML;
			} else {
				this.gridContainer.innerHTML += marker;
			}

		}

	}

	/**
	 * A reusable function to get more slides for any section
	 **/
	async requestGridItems() {
		Logger.info(`${this.NAME} > requestGridItems`);

		try {

			const data = new FormData();
			data.append('action', 'wonder_ajax_get_blog_grid_items');
			data.append('ajax_nonce', window.global_vars.ajax_nonce);
			if (this.authorId) {
				data.append('author_id', this.authorId);
			}
			if (this.categoryId) {
				data.append('category_id', this.categoryId);
			}
			if (this.tagId) {
				data.append('tag_id', this.tagId);
			}
			data.append('paged', this.page);

			const response = await fetch(window.global_vars.ajax_url, {
				method: "POST",
				credentials: 'same-origin',
				body: data
			});

			const responseJson = await response.json();

			return responseJson.data.payload;

		} catch (e) {
			Logger.error(e);
			return false;
		}
	}

	scrollToPaginationMarker(page) {
		Logger.info(`${this.NAME} > scrollToPaginationMarker > ${page}`);
		const element = document.querySelector(`#${this.hash}-${page}`);
		if (element) {
			element.scrollIntoView();
		}
	}

	/**
	 * Set the pagination url parameter.
	 **/
	setPaginationUrlParameter(page) {
		if (this.adjustPageParameter) {
			window.history.replaceState(null, null, `?paged=${page}`);
		}
	}
}

export default ThemeBlogGrid;
